import { AxiosError } from 'axios';

import { ErrorCodeString, IAppErrorResponse } from '@libs/types';

// Accept any function that takes a string and returns a string
// This is compatible with both TypedTranslationFunction and regular t()
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationFunction = (key: any, options?: any) => string;

/**
 * Gets a localized error message from an error object
 *
 * Priority:
 * 1. If errorCode exists → use i18n translation (errors.{errorCode})
 * 2. If no errorCode → use backend message directly (non-localized)
 * 3. If neither → use defaultMessage
 *
 * @param error - The error object
 * @param t - Translation function from useTrans()
 * @param defaultMessage - Fallback message if nothing else is available
 * @returns Localized or backend error message
 *
 * @example
 * ```typescript
 * import { useTrans } from '@libs/i18n';
 * import { useSnackbar } from 'notistack';
 *
 * const { t } = useTrans();
 * const { enqueueSnackbar } = useSnackbar();
 *
 * onError: (error) => {
 *   const message = getLocalizedErrorMessage(error, t);
 *   enqueueSnackbar(message, { variant: 'error' });
 * }
 * ```
 */
export function getLocalizedErrorMessage(
  error: unknown,
  t: TranslationFunction,
  defaultMessage = 'An error occurred',
): string {
  // Try to extract error code from backend
  const errorCode = extractErrorCode(error);

  if (errorCode) {
    // Try to get localized message using error code
    const translationKey = `errors.${errorCode}`;
    const localizedMessage = t(translationKey);

    // If translation exists (key doesn't equal value), use it
    // i18next returns the key itself if translation is missing
    if (localizedMessage && localizedMessage !== translationKey) {
      return localizedMessage;
    }
  }

  // Fallback to backend message (non-localized)
  const backendMessage = extractBackendMessage(error);
  if (backendMessage) {
    return backendMessage;
  }

  // Final fallback to default message
  return defaultMessage;
}

/**
 * Extracts the raw message from backend error response (non-localized)
 * This is used as a fallback when error code translation is not available
 *
 * @param error - The error object
 * @returns Backend error message or undefined
 */
function extractBackendMessage(error: unknown): string | undefined {
  // Handle Axios errors with backend response
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError<IAppErrorResponse>;

    if (axiosError.response?.data) {
      const errorData = axiosError.response.data;

      // New IAppErrorResponse format: { statusCode, errorCode, message, details?, timestamp, path }
      if (typeof errorData === 'object' && 'message' in errorData) {
        const backendError = errorData as IAppErrorResponse;
        if (backendError.message && typeof backendError.message === 'string') {
          return backendError.message;
        }
      }

      // Handle direct error string in response.data (legacy format)
      if (typeof errorData === 'string') {
        return errorData;
      }
    }

    // Check axios error message
    if (axiosError.message) {
      return axiosError.message;
    }
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message;
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Handle objects with message property
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string') {
      return message;
    }
  }

  return undefined;
}

/**
 * Extracts error code from backend error response
 *
 * @param error - The error object to extract error code from
 * @returns Error code string or undefined if not available
 *
 * @example
 * ```typescript
 * try {
 *   await createAssessment();
 * } catch (error) {
 *   const errorCode = extractErrorCode(error);
 *   if (errorCode) {
 *     // Use errorCode to get localized message from i18n
 *     const localizedMessage = t(`errors.${errorCode}`);
 *   }
 * }
 * ```
 */
export function extractErrorCode(error: unknown): ErrorCodeString | undefined {
  // Handle Axios errors with backend response
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError<IAppErrorResponse>;

    if (axiosError.response?.data) {
      const errorData = axiosError.response.data;

      if (typeof errorData === 'object' && 'errorCode' in errorData) {
        const backendError = errorData as IAppErrorResponse;
        if (backendError.errorCode && typeof backendError.errorCode === 'string') {
          return backendError.errorCode;
        }
      }
    }
  }

  return undefined;
}

/**
 * Extracts error details from backend error response
 *
 * @param error - The error object to extract details from
 * @returns Error details object or undefined if not available
 *
 * @example
 * ```typescript
 * const details = extractErrorDetails(error);
 * // details = { field: 'email', reason: 'invalid_format' }
 * ```
 */
export function extractErrorDetails(error: unknown): Record<string, unknown> | undefined {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as AxiosError<IAppErrorResponse>;

    if (axiosError.response?.data) {
      const errorData = axiosError.response.data;

      if (typeof errorData === 'object' && 'details' in errorData) {
        const backendError = errorData as IAppErrorResponse;
        if (backendError.details && typeof backendError.details === 'object') {
          return backendError.details as Record<string, unknown>;
        }
      }
    }
  }

  return undefined;
}